import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import * as authApi from "@/api/auth";
import { useToast } from "@/contexts/ToastContext";

const schema = z.object({
  code: z.string().min(6, "Mínimo 6 caracteres").max(16),
});
type Values = z.infer<typeof schema>;

interface Props {
  mfaSession: string;
  onSuccess: () => Promise<void>;
  onCancel: () => void;
}

export function MfaChallenge({ mfaSession, onSuccess, onCancel }: Props) {
  const toast = useToast();
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
  });

  async function onSubmit(values: Values) {
    try {
      await authApi.verifyMfa({ mfaSession, code: values.code });
      await onSuccess();
    } catch {
      toast.push("Código MFA inválido", "error");
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="card w-full max-w-sm space-y-4"
        data-testid="mfa-form"
      >
        <h2 className="text-xl font-semibold">Verificación MFA</h2>
        <p className="text-sm text-muted-200">
          Ingresa el código de 6 dígitos de tu app autenticadora o un código de
          recuperación de 8 caracteres.
        </p>
        <input
          autoComplete="one-time-code"
          inputMode="numeric"
          autoFocus
          data-testid="mfa-code"
          className="input tracking-widest font-mono"
          {...register("code")}
        />
        {formState.errors.code && (
          <p className="text-xs text-danger">{formState.errors.code.message}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn-ghost flex-1"
            data-testid="mfa-cancel"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={formState.isSubmitting}
            data-testid="mfa-submit"
            className="btn-primary flex-1"
          >
            {formState.isSubmitting ? "Verificando…" : "Verificar"}
          </button>
        </div>
      </form>
    </div>
  );
}
